import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;
const ThemeContext = createContext(null);
export const useWarmTheme = () => useContext(ThemeContext) || { ready: false, flagEnabled: false, effective: 'default', mode: 'light', setMode: () => {}, toggleMode: () => {} };

const applyAttr = (theme) => {
  const el = document.documentElement;
  if (theme && theme !== 'default') el.setAttribute('data-ui-theme', theme);
  else el.removeAttribute('data-ui-theme');
};

const applyMode = (mode) => {
  const el = document.documentElement;
  if (mode === 'dark') el.setAttribute('data-mode', 'dark');
  else el.removeAttribute('data-mode');
};

// Controls the optional Warm Minimal theme. Fetches the flag + the user's saved
// choice, applies the [data-ui-theme] attribute, and exposes toggles. Visual only.
export const WarmThemeProvider = ({ children }) => {
  const { token, isAdmin } = useAuth();
  const [state, setState] = useState({ ready: false, flagEnabled: false, globalDefault: false, myChoice: null, effective: 'default', isAdmin: false });
  const [mode, setModeState] = useState(() => (typeof localStorage !== 'undefined' && localStorage.getItem('ui_mode') === 'dark' ? 'dark' : 'light'));

  useEffect(() => { applyMode(mode); try { localStorage.setItem('ui_mode', mode); } catch { /* ignore */ } }, [mode]);
  const setMode = useCallback((m) => setModeState(m === 'dark' ? 'dark' : 'light'), []);
  const toggleMode = useCallback(() => setModeState((m) => (m === 'dark' ? 'light' : 'dark')), []);

  const headers = useCallback(() => ({ 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }), [token]);

  const load = useCallback(async () => {
    if (!token) { applyAttr('default'); setState((s) => ({ ...s, ready: true })); return; }
    try {
      const r = await fetch(`${API}/api/theme/config`, { headers: headers() });
      if (!r.ok) throw new Error('cfg');
      const d = await r.json();
      applyAttr(d.effective);
      setState({ ready: true, flagEnabled: d.flag_enabled, globalDefault: d.global_default, myChoice: d.my_choice, effective: d.effective, isAdmin: d.is_admin });
    } catch {
      applyAttr('default');
      setState((s) => ({ ...s, ready: true }));
    }
  }, [token, headers]);

  useEffect(() => { load(); }, [load]);

  const setMyTheme = useCallback(async (theme) => {
    applyAttr(theme);  // instant, no reload
    setState((s) => ({ ...s, myChoice: theme, effective: theme }));
    try { await fetch(`${API}/api/theme/me`, { method: 'PUT', headers: headers(), body: JSON.stringify({ theme }) }); }
    catch { /* non-fatal */ }
  }, [headers]);

  const setAdmin = useCallback(async (patch) => {
    try {
      const r = await fetch(`${API}/api/theme/admin`, { method: 'PUT', headers: headers(), body: JSON.stringify(patch) });
      const d = await r.json();
      setState((s) => ({ ...s, flagEnabled: d.enabled, globalDefault: d.default_on }));
      await load();
      return d;
    } catch { /* non-fatal */ }
  }, [headers, load]);

  const value = { ...state, isAdmin: state.isAdmin || (typeof isAdmin === 'function' ? isAdmin() : !!isAdmin), mode, setMode, toggleMode, setMyTheme, setAdmin, reload: load };
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};
